import type { BoxProps, ResponsiveStyleValue } from '@mui/system';
import dynamic from 'next/dynamic';

const Account = dynamic(() => import('./icons/Account'));
const Add = dynamic(() => import('./icons/Add'));
const Appointments = dynamic(() => import('./icons/Appointments'));
const ArrowLeft = dynamic(() => import('./icons/ArrowLeft'));
const ArrowLeftCircle = dynamic(() => import('./icons/ArrowLeftCircle'));
const ArrowRight = dynamic(() => import('./icons/ArrowRight'));
const Billing = dynamic(() => import('./icons/Billing'));
const Cart = dynamic(() => import('./icons/Cart'));
const Call = dynamic(() => import('./icons/Call'));
const Check = dynamic(() => import('./icons/Check'));
const Clock = dynamic(() => import('./icons/Clock'));
const CheckCircleOutline = dynamic(() => import('./icons/CheckCircleOutline'));
const CheckFilled = dynamic(() => import('./icons/CheckFilled'));
const CheckMark = dynamic(() => import('./icons/CheckMark'));
const CheckboxChecked = dynamic(() => import('./icons/CheckboxChecked'));
const CheckboxUnchecked = dynamic(() => import('./icons/CheckboxUnchecked'));
const Chat = dynamic(() => import('./icons/Chat'));
const Chevron = dynamic(() => import('./icons/Chevron'));
const ChevronDown = dynamic(() => import('./icons/ChevronDown'));
const ChevronLeft = dynamic(() => import('./icons/ChevronLeft'));
const ChevronLeftSmall = dynamic(() => import('./icons/ChevronLeftSmall'));
const ChevronRight = dynamic(() => import('./icons/ChevronRight'));
const ChevronUp = dynamic(() => import('./icons/ChevronUp'));
const Close = dynamic(() => import('./icons/Close'));
const CoinInHand = dynamic(() => import('./icons/CoinInHand'));
const CurrencyDollar = dynamic(() => import('./icons/Dollar'));
const Download = dynamic(() => import('./icons/Download'));
const Upload = dynamic(() => import('./icons/Upload'));
const Camera = dynamic(() => import('./icons/Camera'));
const Documents = dynamic(() => import('./icons/Documents'));
const Email = dynamic(() => import('./icons/Email'));
const ErrorFilled = dynamic(() => import('./icons/ErrorFilled'));
const EmptyCircle = dynamic(() => import('./icons/EmptyCircle'));
const ExternalLink = dynamic(() => import('./icons/ExternalLink'));
const Facebook = dynamic(() => import('./icons/Facebook'));
const Grid = dynamic(() => import('./icons/Grid'));
const Headphones = dynamic(() => import('./icons/Headphones'));
const Help = dynamic(() => import('./icons/Help'));
const InfoFilled = dynamic(() => import('./icons/InfoFilled'));
const House = dynamic(() => import('./icons/House'));
const HouseWithPanels = dynamic(() => import('./icons/HouseWithPanels'));
const Impact = dynamic(() => import('./icons/Impact'));
const Home = dynamic(() => import('./icons/Home'));
const Instagram = dynamic(() => import('./icons/Instagram'));
const Lightning = dynamic(() => import('./icons/Lightning'));
const LinkedIn = dynamic(() => import('./icons/LinkedIn'));
const LoadingRing = dynamic(() => import('./icons/LoadingRing'));
const Lock = dynamic(() => import('./icons/Lock'));
const PauseSquare = dynamic(() => import('./icons/PauseSquare'));
const Pencil = dynamic(() => import('./icons/Pencil'));
const Pin = dynamic(() => import('./icons/Pin'));
const Play = dynamic(() => import('./icons/Play'));
const PlaySquare = dynamic(() => import('./icons/PlaySquare'));
const Plus = dynamic(() => import('./icons/Plus'));
const PlusFilled = dynamic(() => import('./icons/PlusFilled'));
const Performance = dynamic(() => import('./icons/Performance'));
const PrivacyOptions = dynamic(() => import('./icons/PrivacyOptions'));
const Referrals = dynamic(() => import('./icons/Referrals'));
const Refresh = dynamic(() => import('./icons/Refresh'));
const Solar = dynamic(() => import('./icons/Solar'));
const Sun = dynamic(() => import('./icons/Sun'));
const Subtract = dynamic(() => import('./icons/Subtract'));
const Twitter = dynamic(() => import('./icons/Twitter'));
const TrashCan = dynamic(() => import('./icons/TrashCan'));
const Warning = dynamic(() => import('./icons/Warning'));
const WarningFilled = dynamic(() => import('./icons/WarningFilled'));
const WarningTriangle = dynamic(() => import('./icons/WarningTriangle'));
const WarningTriangleFilled = dynamic(
  () => import('./icons/WarningTriangleFilled')
);
const Wrench = dynamic(() => import('./icons/Wrench'));
const Monitoring = dynamic(() => import('./icons/Monitoring'));
const Money = dynamic(() => import('./icons/Money'));
const Night = dynamic(() => import('./icons/Night'));
const Year = dynamic(() => import('./icons/Year'));
const Maintenance = dynamic(() => import('./icons/Maintenance'));
const Transfer = dynamic(() => import('./icons/Transfer'));
const EnergyProduction = dynamic(() => import('./icons/EnergyProduction'));
const FullService = dynamic(() => import('./icons/FullService'));
const Battery = dynamic(() => import('./icons/Battery'));
const BatteryFull = dynamic(() => import('./icons/BatteryFull'));
const Star = dynamic(() => import('./icons/Star'));
const StarOutline = dynamic(() => import('./icons/StarOutline'));
const Menu = dynamic(() => import('./icons/Menu'));
const Share = dynamic(() => import('./icons/Share'));
const ChartIncreasing = dynamic(() => import('./icons/ChartIncreasing'));
const ChartDecreasing = dynamic(() => import('./icons/ChartDecreasing'));
const PowerPlant = dynamic(() => import('./icons/PowerPlant'));
const DataCenter = dynamic(() => import('./icons/DataCenter'));
const RadioChecked = dynamic(() => import('./icons/RadioChecked'));
const RadioUnchecked = dynamic(() => import('./icons/RadioUnchecked'));
const Search = dynamic(() => import('./icons/Search'));
const TrendUp = dynamic(() => import('./icons/TrendUp'));
const ViewSquare = dynamic(() => import('./icons/ViewSquare'));

export interface IconSvgProps
  extends Omit<BoxProps<'svg'>, 'width' | 'height' | 'color'> {
  name: string;
  color?: string;
  width?: ResponsiveStyleValue<number>;
  classname?: string;
  backgroundColor?: string;
  innerColor?: string;
  viewBox?: string;
  height?: ResponsiveStyleValue<number>;
}

export enum IconName {
  DOLLAR = 'Dollar',
  WRENCH = 'Wrench',
  PLUS = 'Plus',
  PLUS_FILLED = 'plusFilled',
  CHEVRON = 'Chevron',
  CHART_INCREASING = 'ChartIncreasing',
  CHART_DECREASING = 'ChartDecreasing',
  POWER_PLANT = 'PowerPlant',
  DATA_CENTER = 'DataCenter',
  SEARCH = 'Search',
  TREND_UP = 'TrendUp',
  VIEW_SQUARE = 'ViewSquare',

  // Base Library Icons
  SUN_LINE = 'SunLine',
  LIGHTNING = 'Lightning',
  LOADING_RING = 'LoadingRing',
  CHECK = 'Check',
  CHECK_FILLED = 'CheckFilled',
  CHECK_CIRCLE_OUTLINE = 'CheckCircleOutline',
  CHECK_MARK = 'CheckMark',
  CHECKBOX_CHECKED = 'CheckboxChecked',
  CHECKBOX_UNCHECKED = 'CheckboxUnchecked',
  RADIO_CHECKED = 'RadioChecked',
  RADIO_UNCHECKED = 'RadioUnchecked',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_LEFT_CIRCLE = 'ArrowLeftCircle',
  ARROW_RIGHT = 'ArrowRight',
  ADD = 'Add',
  CLOSE = 'Close',
  CHAT = 'Chat',
  CHEVRON_RIGHT = 'ChevronRight',
  CHEVRON_LEFT = 'ChevronLeft',
  CHEVRON_LEFT_SMALL = 'ChevronLeftSmall',
  CHEVRON_UP = 'ChevronUp',
  CHEVRON_DOWN = 'ChevronDown',
  CART = 'Cart',
  CALL = 'Call',
  CLOCK = 'Clock',
  ACCOUNT = 'Account',
  REFERRALS = 'Referrals',
  REFRESH = 'Refresh',
  IMPACT = 'Impact',
  HOME = 'Home',
  HOUSE = 'House',
  PERFORMANCE = 'Performance',
  DOCUMENTS = 'Documents',
  DOWNLOAD = 'Download',
  UPLOAD = 'Upload',
  CAMERA = 'Camera',
  APPOINTMENTS = 'Appointments',
  EMAIL = 'Email',
  ERROR_FILLED = 'ErrorFilled',
  WARNING = 'Warning',
  WARNING_FILLED = 'WarningFilled',
  WARNING_TRIANGLE = 'WarningTriangle',
  WARNING_TRIANGLE_FILLED = 'WarningTriangleFilled',
  BILLING = 'Billing',
  HEADPHONES = 'Headphones',
  COIN_IN_HAND = 'CoinInHand',
  FACEBOOK = 'Facebook',
  GRID = 'Grid',
  TWITTER = 'Twitter',
  LINKEDIN = 'LinkedIn',
  INSTAGRAM = 'Instagram',
  EXTERNAL_LINK = 'ExternalLink',
  PAUSE_SQUARE = 'PauseSquare',
  PENCIL = 'Pencil',
  PIN = 'Pin',
  PLAY = 'Play',
  PLAY_SQUARE = 'PlaySquare',
  SUBTRACT = 'Subtract',
  EMPTY_CIRCLE = 'EmptyCircle',
  PRIVACY_OPTIONS = 'PrivacyOptions',
  HELP = 'Help',
  INFO_FILLED = 'InfoFilled',
  HOUSE_WITH_PANELS = 'HouseWithPanels',
  MONITORING = 'Monitoring',
  MONEY = 'Money',
  NIGHT = 'Night',
  YEAR = 'Year',
  MAINTENANCE = 'Maintenance',
  TRANSFER = 'Transfer',
  TRASH_CAN = 'TrashCan',
  ENERGY_PRODUCTION = 'EnergyProduction',
  FULL_SERVICE = 'FullService',
  BATTERY = 'Battery',
  BATTERY_FULL = 'BatteryFull',
  STAR = 'Star',
  STAR_OUTLINE = 'StarOutline',
  MENU = 'Menu',
  SHARE = 'Share',
  SOLAR = 'Solar',
  LOCK = 'Lock',
}

function Icon(props: IconSvgProps) {
  const { name } = props;
  switch (name) {
    case IconName.DOLLAR:
      return <CurrencyDollar {...props} />;
    case IconName.WRENCH:
      return <Wrench {...props} />;
    case IconName.CHEVRON:
      return <Chevron {...props} />;
    case IconName.HEADPHONES:
      return <Headphones {...props} />;
    case IconName.SUN_LINE:
      return <Sun {...props} />;
    case IconName.CHART_INCREASING:
      return <ChartIncreasing {...props} />;
    case IconName.CHART_DECREASING:
      return <ChartDecreasing {...props} />;
    case IconName.POWER_PLANT:
      return <PowerPlant {...props} />;
    case IconName.DATA_CENTER:
      return <DataCenter {...props} />;
    case IconName.SEARCH:
      return <Search {...props} />;
    case IconName.TREND_UP:
      return <TrendUp {...props} />;
    case IconName.VIEW_SQUARE:
      return <ViewSquare {...props} />;

    // Base Library Icons
    //
    // Copy from base library and replace the <svg> tag with <BaseLibraryIconSvg/>
    // and change any fill colors to 'currentColor' to automatically pass in correct styles
    case IconName.PLUS:
      return <Plus {...props} />;
    case IconName.PLUS_FILLED:
      return <PlusFilled {...props} />;
    case IconName.CHECK:
      return <Check {...props} />;
    case IconName.CLOCK:
      return <Clock {...props} />;
    case IconName.COIN_IN_HAND:
      return <CoinInHand {...props} />;
    case IconName.CHECK_FILLED:
      return <CheckFilled {...props} />;
    case IconName.CHECK_CIRCLE_OUTLINE:
      return <CheckCircleOutline {...props} />;
    case IconName.CHECK_MARK:
      return <CheckMark {...props} />;
    case IconName.CHECKBOX_CHECKED:
      return <CheckboxChecked {...props} />;
    case IconName.CHECKBOX_UNCHECKED:
      return <CheckboxUnchecked {...props} />;
    case IconName.RADIO_CHECKED:
      return <RadioChecked {...props} />;
    case IconName.RADIO_UNCHECKED:
      return <RadioUnchecked {...props} />;
    case IconName.LIGHTNING:
      return <Lightning {...props} />;
    case IconName.LOADING_RING:
      return <LoadingRing {...props} />;
    case IconName.ARROW_LEFT:
      return <ArrowLeft {...props} />;
    case IconName.ARROW_LEFT_CIRCLE:
      return <ArrowLeftCircle {...props} />;
    case IconName.ARROW_RIGHT:
      return <ArrowRight {...props} />;
    case IconName.ADD:
      return <Add {...props} />;
    case IconName.SUBTRACT:
      return <Subtract {...props} />;
    case IconName.CLOSE:
      return <Close {...props} />;
    case IconName.CHAT:
      return <Chat {...props} />;
    case IconName.CHEVRON_RIGHT:
      return <ChevronRight {...props} />;
    case IconName.CHEVRON_LEFT:
      return <ChevronLeft {...props} />;
    case IconName.CHEVRON_LEFT_SMALL:
      return <ChevronLeftSmall {...props} />;
    case IconName.CHEVRON_UP:
      return <ChevronUp {...props} />;
    case IconName.CHEVRON_DOWN:
      return <ChevronDown {...props} />;
    case IconName.CART:
      return <Cart {...props} />;
    case IconName.CALL:
      return <Call {...props} />;
    case IconName.ACCOUNT:
      return <Account {...props} />;
    case IconName.REFERRALS:
      return <Referrals {...props} />;
    case IconName.REFRESH:
      return <Refresh {...props} />;
    case IconName.IMPACT:
      return <Impact {...props} />;
    case IconName.HOME:
      return <Home {...props} />;
    case IconName.HOUSE:
      return <House {...props} />;
    case IconName.PERFORMANCE:
      return <Performance {...props} />;
    case IconName.DOCUMENTS:
      return <Documents {...props} />;
    case IconName.DOWNLOAD:
      return <Download {...props} />;
    case IconName.UPLOAD:
      return <Upload {...props} />;
    case IconName.CAMERA:
      return <Camera {...props} />;
    case IconName.EMAIL:
      return <Email {...props} />;
    case IconName.ERROR_FILLED:
      return <ErrorFilled {...props} />;
    case IconName.APPOINTMENTS:
      return <Appointments {...props} />;
    case IconName.WARNING:
      return <Warning {...props} />;
    case IconName.WARNING_FILLED:
      return <WarningFilled {...props} />;
    case IconName.WARNING_TRIANGLE:
      return <WarningTriangle {...props} />;
    case IconName.WARNING_TRIANGLE_FILLED:
      return <WarningTriangleFilled {...props} />;
    case IconName.BILLING:
      return <Billing {...props} />;
    case IconName.FACEBOOK:
      return <Facebook {...props} />;
    case IconName.GRID:
      return <Grid {...props} />;
    case IconName.INSTAGRAM:
      return <Instagram {...props} />;
    case IconName.LINKEDIN:
      return <LinkedIn {...props} />;
    case IconName.TWITTER:
      return <Twitter {...props} />;
    case IconName.EXTERNAL_LINK:
      return <ExternalLink {...props} />;
    case IconName.EMPTY_CIRCLE:
      return <EmptyCircle {...props} />;
    case IconName.PENCIL:
      return <Pencil {...props} />;
    case IconName.PIN:
      return <Pin {...props} />;
    case IconName.PLAY:
      return <Play {...props} />;
    case IconName.PRIVACY_OPTIONS:
      return <PrivacyOptions {...props} />;
    case IconName.HELP:
      return <Help {...props} />;
    case IconName.INFO_FILLED:
      return <InfoFilled {...props} />;
    case IconName.HOUSE_WITH_PANELS:
      return <HouseWithPanels {...props} />;
    case IconName.PAUSE_SQUARE:
      return <PauseSquare {...props} />;
    case IconName.PLAY_SQUARE:
      return <PlaySquare {...props} />;
    case IconName.MONITORING:
      return <Monitoring {...props} />;
    case IconName.MONEY:
      return <Money {...props} />;
    case IconName.YEAR:
      return <Year {...props} />;
    case IconName.MAINTENANCE:
      return <Maintenance {...props} />;
    case IconName.NIGHT:
      return <Night {...props} />;
    case IconName.TRANSFER:
      return <Transfer {...props} />;
    case IconName.TRASH_CAN:
      return <TrashCan {...props} />;
    case IconName.ENERGY_PRODUCTION:
      return <EnergyProduction {...props} />;
    case IconName.FULL_SERVICE:
      return <FullService {...props} />;
    case IconName.BATTERY:
      return <Battery {...props} />;
    case IconName.BATTERY_FULL:
      return <BatteryFull {...props} />;
    case IconName.STAR:
      return <Star {...props} />;
    case IconName.STAR_OUTLINE:
      return <StarOutline {...props} />;
    case IconName.MENU:
      return <Menu {...props} />;
    case IconName.SHARE:
      return <Share {...props} />;
    case IconName.SOLAR:
      return <Solar {...props} />;
    case IconName.LOCK:
      return <Lock {...props} />;
    // Return null for unsupported icon names
    default:
      return null;
  }
}
export default Icon;
